import {FC, Fragment, useCallback, useEffect, useState} from 'react'
import {
  listApiKeys,
  createApiKey,
  updateApiKey,
  regenerateApiKey,
  deleteApiKey,
  getApiKeyAccounts,
  ApiKey,
  ApiKeyAccount,
} from './_requests'

const err = (e: any, fallback = 'שגיאה') => e?.response?.data?.error || e?.message || fallback

const fmt = (d?: string | null) => {
  if (!d) return '—'
  const x = new Date(d)
  return isNaN(x.getTime())
    ? '—'
    : x.toLocaleString('he-IL', {day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'})
}

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e6e9f0',
  padding: 18,
  marginBottom: 16,
}
const chip = (bg: string, color = '#fff'): React.CSSProperties => ({
  background: bg,
  color,
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 12,
  fontWeight: 700,
  display: 'inline-block',
})

const ApiKeysPage: FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [endpoint, setEndpoint] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [newName, setNewName] = useState('')
  const [newNotes, setNewNotes] = useState('')
  const [creating, setCreating] = useState(false)
  // The plaintext key is returned exactly once — hold it until it's dismissed.
  const [secret, setSecret] = useState<{name: string; value: string} | null>(null)
  const [copied, setCopied] = useState(false)

  const [openAccounts, setOpenAccounts] = useState<string>('')
  const [accounts, setAccounts] = useState<ApiKeyAccount[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const {data} = await listApiKeys()
      setKeys(data.keys || [])
      setEndpoint(data.endpoint || '')
    } catch (e: any) {
      setError(err(e, 'שגיאה בטעינת המפתחות'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const doCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const {data} = await createApiKey({name: newName.trim(), notes: newNotes.trim()})
      setSecret({name: data.key.name, value: data.secret})
      setNewName('')
      setNewNotes('')
      await load()
    } catch (e: any) {
      setError(err(e))
    } finally {
      setCreating(false)
    }
  }

  const doRegenerate = async (k: ApiKey) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`ליצור מפתח חדש ל"${k.name}"? המפתח הנוכחי יפסיק לעבוד מיד.`)) return
    try {
      const {data} = await regenerateApiKey(k.id)
      setSecret({name: data.key.name, value: data.secret})
      await load()
    } catch (e: any) {
      setError(err(e))
    }
  }

  const doToggle = async (k: ApiKey) => {
    try {
      await updateApiKey(k.id, {active: !k.active} as any)
      await load()
    } catch (e: any) {
      setError(err(e))
    }
  }

  const doDelete = async (k: ApiKey) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`למחוק את "${k.name}"? האפליקציה שמשתמשת בו תפסיק לעבוד.`)) return
    try {
      await deleteApiKey(k.id)
      await load()
    } catch (e: any) {
      setError(err(e))
    }
  }

  const toggleAccounts = async (k: ApiKey) => {
    if (openAccounts === k.id) {
      setOpenAccounts('')
      return
    }
    setOpenAccounts(k.id)
    setAccounts([])
    try {
      const {data} = await getApiKeyAccounts(k.id)
      setAccounts(data.accounts || [])
    } catch (e: any) {
      setError(err(e))
    }
  }

  const copySecret = () => {
    if (!secret) return
    navigator.clipboard?.writeText(secret.value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{padding: 20}} dir='rtl'>
      <div className='mb-4'>
        <h1 style={{margin: 0, fontSize: 26, fontWeight: 800}}>מפתחות API</h1>
        <div style={{opacity: 0.6, fontSize: 14}}>
          מאפשר לאפליקציה חיצונית לרשום לקוחות למערכת. כל הרשמה נספרת למפתח שיצר אותה.
        </div>
      </div>

      {error && <div className='alert alert-danger'>{error}</div>}

      {secret && (
        <div style={{...card, border: '2px solid #0b8a3d', background: '#f4fbf6'}}>
          <div style={{fontWeight: 800, marginBottom: 6}}>
            <i className='bi bi-key me-1'></i> המפתח של "{secret.name}"
          </div>
          <div style={{fontSize: 13, color: '#c0392b', fontWeight: 700, marginBottom: 10}}>
            זו הפעם היחידה שהמפתח מוצג. העתיקו אותו עכשיו — לא ניתן לשחזר אותו, רק ליצור חדש.
          </div>
          <div className='d-flex gap-2 align-items-center flex-wrap'>
            <code
              dir='ltr'
              style={{background: '#fff', border: '1px solid #cfe3d6', borderRadius: 8, padding: '10px 14px', fontSize: 14, flex: 1, minWidth: 280}}
            >
              {secret.value}
            </code>
            <button className='btn btn-sm btn-success' onClick={copySecret}>
              <i className='bi bi-clipboard me-1'></i>
              {copied ? 'הועתק ✓' : 'העתק'}
            </button>
            <button className='btn btn-sm btn-outline-secondary' onClick={() => setSecret(null)}>
              סגור
            </button>
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 10}}>מפתח חדש</div>
        <div className='row g-2'>
          <div className='col-md-4'>
            <input
              className='form-control form-control-sm'
              placeholder='שם (למשל: אתר הנדל"ן שלי)'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doCreate()}
            />
          </div>
          <div className='col-md-6'>
            <input
              className='form-control form-control-sm'
              placeholder='הערה (אופציונלי)'
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
            />
          </div>
          <div className='col-md-2'>
            <button className='btn btn-primary btn-sm w-100' onClick={doCreate} disabled={creating || !newName.trim()}>
              {creating ? 'יוצר...' : 'צור מפתח'}
            </button>
          </div>
        </div>
      </div>

      <div style={card}>
        <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
          <div style={{fontWeight: 800}}>המפתחות ({keys.length})</div>
          {endpoint && (
            <code dir='ltr' style={{fontSize: 12, background: '#f4f6fa', borderRadius: 6, padding: '4px 10px'}}>
              POST {endpoint}
            </code>
          )}
        </div>
        {loading ? (
          <div className='text-center p-4'>
            <span className='spinner-border spinner-border-sm' />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className='table table-sm align-middle'>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>מפתח</th>
                  <th>סטטוס</th>
                  <th>הרשמות</th>
                  <th>שימוש אחרון</th>
                  <th>נוצר</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <Fragment key={k.id}>
                    <tr>
                      <td>
                        <div style={{fontWeight: 700}}>{k.name}</div>
                        {k.notes && <div style={{fontSize: 12, opacity: 0.6}}>{k.notes}</div>}
                      </td>
                      <td dir='ltr'>
                        <code style={{fontSize: 12}}>{k.key_prefix}…</code>
                      </td>
                      <td>
                        <span style={chip(k.active ? '#0b8a3d' : '#c0392b')}>{k.active ? 'פעיל' : 'מבוטל'}</span>
                      </td>
                      <td>
                        <button className='btn btn-sm btn-link p-0' onClick={() => toggleAccounts(k)}>
                          <b>{k.accounts_created}</b>
                        </button>
                      </td>
                      <td>
                        {fmt(k.last_used_at)}
                        {k.last_used_ip && <div style={{fontSize: 11, opacity: 0.6}} dir='ltr'>{k.last_used_ip}</div>}
                      </td>
                      <td>{fmt(k.created_at)}</td>
                      <td className='text-end' style={{whiteSpace: 'nowrap'}}>
                        <button className='btn btn-sm btn-outline-secondary me-1' title='מפתח חדש' onClick={() => doRegenerate(k)}>
                          <i className='bi bi-arrow-repeat'></i>
                        </button>
                        <button
                          className={`btn btn-sm me-1 ${k.active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          title={k.active ? 'בטל' : 'הפעל'}
                          onClick={() => doToggle(k)}
                        >
                          <i className={`bi ${k.active ? 'bi-slash-circle' : 'bi-check-circle'}`}></i>
                        </button>
                        <button className='btn btn-sm btn-outline-danger' onClick={() => doDelete(k)}>
                          <i className='bi bi-trash'></i>
                        </button>
                      </td>
                    </tr>
                    {openAccounts === k.id && (
                      <tr>
                        <td colSpan={7} style={{background: '#f8fafc'}}>
                          <div style={{fontWeight: 700, marginBottom: 6}}>לקוחות שנרשמו דרך המפתח</div>
                          <table className='table table-sm mb-0'>
                            <thead>
                              <tr>
                                <th>שם</th>
                                <th>אימייל</th>
                                <th>טלפון</th>
                                <th>נרשם</th>
                                <th>ניסיון עד</th>
                              </tr>
                            </thead>
                            <tbody>
                              {accounts.map((a) => (
                                <tr key={a.id}>
                                  <td>{a.name || '—'}</td>
                                  <td dir='ltr'>{a.email}</td>
                                  <td dir='ltr'>{a.phone}</td>
                                  <td>{fmt(a.created_at)}</td>
                                  <td>{fmt(a.trial_ends_at)}</td>
                                </tr>
                              ))}
                              {!accounts.length && (
                                <tr>
                                  <td colSpan={5} className='text-center' style={{opacity: 0.6}}>אין עדיין הרשמות</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!keys.length && (
                  <tr>
                    <td colSpan={7} className='text-center' style={{opacity: 0.6}}>אין מפתחות עדיין</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ApiKeysPage
